import { useState, useEffect, useCallback } from 'react';

let globalWishlistItems = [];
const listeners = new Set();

const updateGlobalWishlist = (newItems) => {
  globalWishlistItems = newItems;
  listeners.forEach(listener => listener(newItems));
};

/**
 * Custom hook for wishlist operations.
 * Manages wishlist state backed by /api/wishlist (MongoDB).
 * Falls back gracefully when user is not logged in.
 */
const useWishlist = () => {
  const [wishlistItems, setWishlistItems] = useState(globalWishlistItems);
  const [loadingIds, setLoadingIds] = useState(new Set());

  useEffect(() => {
    const handleUpdate = (items) => {
      setWishlistItems(items);
    };
    listeners.add(handleUpdate);
    setWishlistItems(globalWishlistItems);
    return () => {
      listeners.delete(handleUpdate);
    };
  }, []);

  const getUser = () => {
    try {
      const session = localStorage.getItem('user_session');
      return session ? JSON.parse(session) : null;
    } catch {
      return null;
    }
  };

  // Load wishlist from backend on mount / when user changes
  const loadWishlist = useCallback(async () => {
    const user = getUser();
    if (!user || !user.email) {
      updateGlobalWishlist([]);
      return;
    }
    try {
      const res = await fetch(`/api/wishlist/${encodeURIComponent(user.email)}`);
      if (res.ok) {
        const data = await res.json();
        updateGlobalWishlist(Array.isArray(data.items) ? data.items : []);
      }
    } catch (err) {
      console.error('[Wishlist] Failed to load:', err);
    }
  }, []);

  useEffect(() => {
    loadWishlist();
    const handleAuthChange = () => loadWishlist();
    window.addEventListener('auth_change', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    return () => {
      window.removeEventListener('auth_change', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [loadWishlist]);

  /** Returns true if product is in wishlist */
  const isWishlisted = useCallback(
    (productId) => wishlistItems.some(item => item.productId === productId),
    [wishlistItems]
  );

  /** Toggle a product in / out of the wishlist */
  const toggleWishlist = useCallback(async (product) => {
    const user = getUser();
    if (!user || !user.email) {
      // Prompt user to login
      window.history.pushState({}, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }

    const productId = String(product.productId || product._id || product.id);
    if (loadingIds.has(productId)) return; // debounce

    setLoadingIds(prev => new Set([...prev, productId]));

    // Optimistic toggle in UI
    const nextItems = (() => {
      const exists = globalWishlistItems.some(item => item.productId === productId);
      if (exists) return globalWishlistItems.filter(item => item.productId !== productId);
      return [...globalWishlistItems, {
        productId,
        name: product.name || '',
        image: product.image || '',
        price: product.price || 0,
        category: product.category || '',
        addedAt: new Date().toISOString(),
      }];
    })();
    updateGlobalWishlist(nextItems);

    try {
      await fetch('/api/wishlist/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          product: {
            productId,
            name: product.name || '',
            image: product.image || '',
            price: product.price || 0,
            category: product.category || '',
          }
        })
      });
    } catch (err) {
      console.error('[Wishlist] Toggle failed:', err);
      loadWishlist(); // Rollback optimistic update
    } finally {
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  }, [loadingIds, loadWishlist]);

  return { wishlistItems, isWishlisted, toggleWishlist, loadingIds, loadWishlist };
};

export default useWishlist;
